import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { Search, Copy, Trash2, Download, Check } from 'lucide-react';
import { useToast } from '../components/ui/Feedback';
import { friendlyError } from "../lib/errorMessage";

type WaitlistEntry = {
  id: string;
  email: string;
  created_at: string;
};

export default function Waitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => { fetchEntries(); }, []);

  async function fetchEntries() {
    const { data } = await supabase
      .from('waitlist')
      .select('*')
      .order('created_at', { ascending: false });
    setEntries(data ?? []);
    setLoading(false);
  }

  async function removeEntry(id: string) {
    if (!window.confirm('Remove this email from the waitlist?')) return;
    // The error was discarded here and the row removed from local state
    // regardless, so this button appeared to work for as long as it didn't —
    // waitlist had no DELETE policy at all until migration 0011.
    const { error } = await supabase.from('waitlist').delete().eq('id', id);
    if (error) {
      toast.failure(friendlyError(error, "Something went wrong. Please try again."));
      return;
    }
    toast.success('Removed from waitlist');
    setEntries(e => e.filter(x => x.id !== id));
  }

  function copyEmail(id: string, email: string) {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  }

  function exportCsv() {
    const rows = [['email', 'joined_at'], ...filtered.map(e => [e.email, e.created_at])];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const isToday = (d: string) => new Date(d) >= startOfToday;
  const isThisWeek = (d: string) => new Date(d) >= startOfWeek;
  const isThisMonth = (d: string) => new Date(d) >= startOfMonth;

  const filtered = entries.filter(e => {
    const matchSearch = e.email?.toLowerCase().includes(search.toLowerCase());
    if (filter === 'today') return matchSearch && isToday(e.created_at);
    if (filter === 'week') return matchSearch && isThisWeek(e.created_at);
    if (filter === 'month') return matchSearch && isThisMonth(e.created_at);
    return matchSearch;
  });

  const initials = (email: string) => email?.slice(0, 2).toUpperCase() ?? '?';
  const avatarColors = ['#6c5ffc', '#f16d75', '#19b159', '#f7b731', '#4ec2f0'];
  const colorFor = (id: string) => avatarColors[id.charCodeAt(0) % avatarColors.length];

  return (
    <div>
      <Topbar title="Waitlist" />
      <div style={{ padding: '24px 28px' }}>

        {/* Summary */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Signups', count: entries.length, key: 'all', color: '#6c5ffc' },
            { label: 'Today', count: entries.filter(e => isToday(e.created_at)).length, key: 'today', color: '#19b159' },
            { label: 'This Week', count: entries.filter(e => isThisWeek(e.created_at)).length, key: 'week', color: '#f7b731' },
            { label: 'This Month', count: entries.filter(e => isThisMonth(e.created_at)).length, key: 'month', color: '#4ec2f0' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key as any)} style={{
              flex: 1, padding: '14px 16px',
              background: filter === f.key ? f.color : '#fff',
              color: filter === f.key ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${filter === f.key ? f.color : 'var(--border)'}`,
              borderRadius: 'var(--radius)',
              cursor: 'pointer', transition: 'all 0.2s',
              textAlign: 'left',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{f.count}</div>
              <div style={{ fontSize: 12, marginTop: 2, opacity: filter === f.key ? 0.85 : 1 }}>{f.label}</div>
            </button>
          ))}
        </div>

        <div className="card">
          {/* Search + Export */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input className="input" placeholder="Search by email..."
                style={{ paddingLeft: 34 }} value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="btn btn-sm" onClick={exportCsv} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--primary-light)', color: 'var(--primary)', border: 'none',
              padding: '0 14px',
            }}>
              <Download size={13} /> Export CSV
            </button>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(entry => (
                  <tr key={entry.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar" style={{ background: colorFor(entry.id), color: '#fff', fontSize: 12 }}>
                          {initials(entry.email)}
                        </div>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.email}</span>
                      </div>
                    </td>
                    <td>
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm" title="Copy email"
                          onClick={() => copyEmail(entry.id, entry.email)}
                          style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: 'none' }}>
                          {copiedId === entry.id ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                        <button className="btn btn-sm" title="Remove from waitlist"
                          onClick={() => removeEntry(entry.id)}
                          style={{ background: 'var(--danger-light)', color: 'var(--danger)', border: 'none' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No signups found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}